import React, { useState, useCallback, useEffect } from 'react'
import { useDropzone } from 'react-dropzone'
import { Link } from 'react-router-dom'
import toast from 'react-hot-toast'
import { useTranslation } from 'react-i18next'
import { useImages } from '@/contexts/ImageContext'
import { ChunkedUploader } from '@/components/upload/ChunkedUpload'
import { logger } from '@/utils/logger'

interface UploadFile {
  id: string
  file: File
  progress: number
  status: 'pending' | 'uploading' | 'processing' | 'completed' | 'error'
  error?: string
  imageId?: string
  uploadMethod?: 'direct' | 'chunked'
}

const SimpleImageUpload: React.FC = () => {
  const { t } = useTranslation()
  const { addImage, refreshImages } = useImages()
  const [files, setFiles] = useState<UploadFile[]>([])
  const [isUploading, setIsUploading] = useState(false)

  // Patient-centric workflow
  const [patients, setPatients] = useState<any[]>([])
  const [selectedPatient, setSelectedPatient] = useState<string>('')
  const [patientSearchTerm, setPatientSearchTerm] = useState('')

  // Specimen metadata
  const [specimenType, setSpecimenType] = useState<string>('')
  const [anatomicSite, setAnatomicSite] = useState<string>('')
  const [slideBarcode, setSlideBarcode] = useState<string>('')

  useEffect(() => {
    // Fetch patients
    const fetchData = async () => {
      try {
        const patientsResponse = await fetch('/api/v1/pathology/patients/')
        if (patientsResponse.ok) {
          const patientsData = await patientsResponse.json()
          setPatients(patientsData.results || patientsData)
        }
      } catch (error) {
        console.error('Error fetching patients:', error)
      }
    }
    fetchData()
  }, [])

  const onDrop = useCallback((acceptedFiles: File[]) => {
    logger.debug('📁 Files selected:', acceptedFiles)

    const newFiles: UploadFile[] = acceptedFiles.map((file, index) => {
      // Use chunked upload for files > 50MB, direct upload for smaller files
      const fileSizeMB = file.size / 1024 / 1024
      const useChunkedUpload = fileSizeMB > 50

      logger.debug(`📦 File: ${file.name}, Size: ${fileSizeMB.toFixed(2)}MB, Method: ${useChunkedUpload ? 'CHUNKED' : 'DIRECT'}`)

      return {
        id: `${file.name}-${Date.now()}-${index}`,
        file: file,
        progress: 0,
        status: 'pending' as const,
        uploadMethod: useChunkedUpload ? 'chunked' : 'direct'
      }
    })

    setFiles(prev => [...prev, ...newFiles])
  }, [])

  const { getRootProps, getInputProps, isDragActive} = useDropzone({
    onDrop,
    accept: {
      // Supported whole slide imaging formats
      'image/*': [
        '.svs',      // Aperio
        '.tif',      // Aperio, Trestle, Generic TIFF
        '.tiff',     // Philips, Generic TIFF
        '.ndpi',     // Hamamatsu NDPI
        '.vms',      // Hamamatsu VMS
        '.vmu',      // Hamamatsu VMU
        '.scn',      // Leica
        '.mrxs',     // MIRAX (3DHISTECH)
        '.svslide',  // Sakura
        '.bif',      // Ventana
        '.dcm',      // DICOM WSI (OpenSlide 4.0+)
      ],
      'application/zip': ['.zip']  // DICOM multi-file archives
    },
    multiple: true,
    maxSize: 10 * 1024 * 1024 * 1024 // 10GB
  })

  const uploadFile = async (uploadFile: UploadFile) => {
    const { file, uploadMethod } = uploadFile

    if (!file || !file.name) {
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id ? {
          ...f,
          status: 'error',
          error: 'Invalid file'
        } : f
      ))
      return
    }

    // Use chunked upload for large files
    if (uploadMethod === 'chunked') {
      return uploadFileChunked(uploadFile)
    }

    // Continue with direct upload for small files
    return uploadFileDirect(uploadFile)
  }

  const uploadFileChunked = async (uploadFile: UploadFile) => {
    const { file } = uploadFile

    try {
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id ? { ...f, status: 'uploading' } : f
      ))

      logger.debug('🚀 Starting chunked upload:', file.name, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`)

      const uploader = new ChunkedUploader({
        file,
        patient: selectedPatient || undefined,
        slideBarcode: slideBarcode || undefined,
        specimenType: specimenType || undefined,
        anatomicSite: anatomicSite || undefined,
        onProgress: (progress) => {
          setFiles(prev => prev.map(f =>
            f.id === uploadFile.id ? { ...f, progress } : f
          ))
        },
        onComplete: (result) => {
          // Handle successful upload
          setFiles(prev => prev.map(f =>
            f.id === uploadFile.id ? {
              ...f,
              status: 'processing',
              progress: 100,
              imageId: result.image?.id
            } : f
          ))

          // Add image to context if we got the result
          if (result.image) {
            addImage({
              id: result.image.id,
              image_id: result.image.image_id,
              filename: result.image.filename,
              image_type: result.image.image_type,
              image_format: result.image.image_format,
              width: result.image.width || 0,
              height: result.image.height || 0,
              magnification: result.image.magnification || 0,
              slide_id: result.image.slide_id,
              case_id: result.image.case_id,
              file_size_mb: result.image.file_size_mb,
              created_at: result.image.created_at,
              scanner_model: result.image.scanner_model || '',
              annotations_count: 0,
              resolution_mpx: result.image.resolution_mpx || 0,
              status: 'processing' as const
            })
          }

          // Mark as completed
          setTimeout(() => {
            setFiles(prev => prev.map(f =>
              f.id === uploadFile.id ? { ...f, status: 'completed' } : f
            ))
            // Refresh image library to show new upload
            refreshImages()
          }, 1000)

          toast.success(t('upload.uploadCompleted', { fileName: file.name }))
        },
        onError: (error) => {
          console.error('Chunked upload error details:', error)
          let errorMessage = error.message

          // Provide more specific error messages
          if (errorMessage.includes('Missing chunks')) {
            errorMessage = 'Upload incomplete - some file chunks were lost. Please try uploading again.'
          } else if (errorMessage.includes('Failed to finalize')) {
            errorMessage = 'Upload processing failed. Please check your connection and try again.'
          } else if (errorMessage.includes('timeout')) {
            errorMessage = 'Upload timed out. Please check your connection and try again.'
          }

          setFiles(prev => prev.map(f =>
            f.id === uploadFile.id ? {
              ...f,
              status: 'error',
              error: errorMessage
            } : f
          ))
          toast.error(t('upload.uploadFailed', { error: errorMessage }))
        }
      })

      await uploader.upload()

    } catch (error: any) {
      console.error('Chunked upload error:', error)
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id ? {
          ...f,
          status: 'error',
          error: error.message
        } : f
      ))
      toast.error(t('upload.uploadFailed', { error: error.message }))
    }
  }

  const uploadFileDirect = async (uploadFile: UploadFile) => {
    const { file } = uploadFile

    const formData = new FormData()
    formData.append('file', file)
    formData.append('slide_id', 'SL-' + Date.now())
    formData.append('image_type', 'WSI')

    // Patient-centric workflow
    if (selectedPatient) {
      formData.append('patient', selectedPatient)
    }

    // Specimen metadata
    if (specimenType) {
      formData.append('specimen_type', specimenType)
    }
    if (anatomicSite) {
      formData.append('anatomic_site', anatomicSite)
    }

    // Slide barcode (optional)
    if (slideBarcode) {
      formData.append('slide_barcode', slideBarcode)
    }

    const fileName = file.name
    let fileExtension = 'UNKNOWN'
    if (fileName.includes('.')) {
      const parts = fileName.split('.')
      fileExtension = parts[parts.length - 1].toUpperCase()
    }
    formData.append('image_format', fileExtension)

    try {
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id ? { ...f, status: 'uploading' } : f
      ))

      logger.debug('🚀 Starting upload:', fileName, `(${(file.size / 1024 / 1024).toFixed(2)} MB)`)

      const xhr = new XMLHttpRequest()

      const uploadPromise = new Promise<Response>((resolve, reject) => {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable) {
            const percentComplete = Math.round((event.loaded / event.total) * 100)
            setFiles(prev => prev.map(f =>
              f.id === uploadFile.id ? { ...f, progress: percentComplete } : f
            ))
          }
        }

        xhr.onload = () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve(new Response(xhr.responseText, { status: xhr.status }))
          } else {
            reject(new Error(`HTTP ${xhr.status}: ${xhr.statusText}`))
          }
        }

        xhr.onerror = () => reject(new Error(`Network error: ${xhr.status} ${xhr.statusText}`))
        xhr.ontimeout = () => reject(new Error(`Upload timeout after ${xhr.timeout}ms`))

        xhr.open('POST', '/api/v1/imaging/standalone/upload/')
        xhr.timeout = 3600000 // 60 minutes
        xhr.send(formData)
      })

      const response = await uploadPromise

      if (!response.ok) {
        const errorText = await response.text()
        throw new Error(`Upload failed: ${response.statusText} - ${errorText}`)
      }

      const result = await response.json()

      logger.debug('Upload result:', result)

      // Standalone API returns slide object directly
      const slide = result

      // Update to processing status
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id ? {
          ...f,
          status: 'processing',
          progress: 100,
          imageId: slide.id
        } : f
      ))

      // Add image to context
      addImage({
        id: slide.id,
        image_id: slide.id,
        filename: slide.name,
        image_type: 'WSI',
        image_format: slide.file_format?.replace('.', '').toUpperCase() || 'SVS',
        width: slide.width || 0,
        height: slide.height || 0,
        magnification: 0,
        slide_id: `SL-${slide.id.substr(0,8)}`,
        case_id: `PC-${slide.id.substr(0,8)}`,
        file_size_mb: Math.round((slide.file_size || 0) / 1024 / 1024 * 100) / 100,
        created_at: slide.created_at,
        scanner_model: '',
        annotations_count: 0,
        resolution_mpx: Math.round((slide.width * slide.height) / 1000000 * 100) / 100 || 0,
        status: 'processing' as const
      })

      // Update to completed status
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id ? { ...f, status: 'completed' } : f
      ))

      // Refresh image library to show new upload
      refreshImages()

      toast.success(t('upload.uploadCompleted', { fileName }))

    } catch (error: any) {
      console.error('Upload error:', error)
      setFiles(prev => prev.map(f =>
        f.id === uploadFile.id ? {
          ...f,
          status: 'error',
          error: error.message
        } : f
      ))
      toast.error(t('upload.uploadFailed', { error: error.message }))
    }
  }

  const uploadAllFiles = async () => {
    setIsUploading(true)
    const pendingFiles = files.filter(f => f.status === 'pending')

    for (const file of pendingFiles) {
      await uploadFile(file)
    }

    setIsUploading(false)
  }

  const removeFile = (fileId: string) => {
    setFiles(prev => prev.filter(f => f.id !== fileId))
  }

  const clearCompleted = () => {
    setFiles(prev => prev.filter(f => f.status !== 'completed'))
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'pending': return '⏳'
      case 'uploading': return '📤'
      case 'processing': return '⚙️'
      case 'completed': return '✅'
      case 'error': return '❌'
      default: return '📄'
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'pending': return 'text-gray-600'
      case 'uploading': return 'text-blue-600'
      case 'processing': return 'text-yellow-600'
      case 'completed': return 'text-green-600'
      case 'error': return 'text-red-600'
      default: return 'text-gray-600'
    }
  }

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="bg-white rounded-lg shadow-sm border">
        <div className="p-6 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {t('upload.title')}
              </h1>
              <p className="text-gray-600 mt-1">
                {t('image.uploadSystem')}
              </p>
            </div>
            <Link
              to="/imaging/library"
              className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              {t('image.viewLibrary')}
            </Link>
          </div>
        </div>

        {/* Upload Area */}
        <div className="p-6">
          {/* Metadata Section - Patient-centric Workflow */}
          <div className="mb-6 space-y-4">
            {/* Primary: Patient Selection */}
            <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-4">
              <label className="block text-sm font-semibold text-blue-900 mb-2">
                {t('upload.linkToPatient')}
              </label>
              <input
                type="text"
                value={patientSearchTerm}
                onChange={(e) => setPatientSearchTerm(e.target.value)}
                placeholder={t('upload.searchPatient')}
                className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white mb-2"
              />
              <select
                value={selectedPatient}
                onChange={(e) => {
                  setSelectedPatient(e.target.value)
                  const patient = patients.find(p => p.id == e.target.value)
                  if (patient) {
                    setPatientSearchTerm(`${patient.full_name} (${patient.patient_id})`)
                  }
                }}
                className="w-full px-3 py-2 border border-blue-300 rounded-md focus:ring-blue-500 focus:border-blue-500 bg-white"
              >
                <option value="">{t('upload.selectPatient')}</option>
                {patients
                  .filter(p => {
                    if (!patientSearchTerm) return true
                    const search = patientSearchTerm.toLowerCase()
                    return p.full_name?.toLowerCase().includes(search) ||
                           p.patient_id?.toLowerCase().includes(search)
                  })
                  .map(p => (
                    <option key={p.id} value={p.id}>
                      {p.full_name} ({p.patient_id})
                    </option>
                  ))}
              </select>
              {selectedPatient && (
                <p className="mt-2 text-xs text-green-700 font-medium">
                  {t('upload.imagesLinkedToPatient', { patientName: patients.find(p => p.id == selectedPatient)?.full_name })}
                </p>
              )}
            </div>

            {/* Specimen Metadata */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('upload.specimenType')}
                </label>
                <select
                  value={specimenType}
                  onChange={(e) => setSpecimenType(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">Select type...</option>
                  <option value="TISSUE">{t('upload.tissue')}</option>
                  <option value="BIOPSY">{t('upload.biopsy')}</option>
                  <option value="CYTOLOGY">{t('upload.cytology')}</option>
                  <option value="FLUID">{t('upload.fluid')}</option>
                  <option value="OTHER">{t('upload.other')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('upload.anatomicSite')}
                </label>
                <input
                  type="text"
                  value={anatomicSite}
                  onChange={(e) => setAnatomicSite(e.target.value)}
                  placeholder="e.g., Lung, Breast, Colon"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            {/* Optional: Slide Barcode */}
            <details className="border border-gray-200 rounded-md p-3">
              <summary className="text-sm font-medium text-gray-600 cursor-pointer">
                {t('upload.advancedOptions')}
              </summary>
              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  {t('upload.slideBarcode')}
                </label>
                <input
                  type="text"
                  value={slideBarcode}
                  onChange={(e) => setSlideBarcode(e.target.value)}
                  placeholder={t('upload.autoGenerated')}
                  className="w-full px-2 py-1 text-sm border border-gray-300 rounded-md"
                />
              </div>
            </details>
          </div>

          <div
            {...getRootProps()}
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors cursor-pointer
              ${isDragActive
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-300 hover:border-gray-400'
              }`}
          >
            <input {...getInputProps()} />
            <div className="space-y-4">
              <div className="text-6xl">📤</div>
              <div>
                <p className="text-lg font-medium text-gray-700">
                  {isDragActive
                    ? 'Drop files here...'
                    : t('image.dragDropFiles')
                  }
                </p>
                <p className="text-gray-500 mt-1">
                  {t('image.orClickBrowse')}
                </p>
                <p className="text-sm text-gray-400 mt-2">
                  {t('image.allSizesSupported')}
                </p>
              </div>
            </div>
          </div>

          {/* Control Buttons */}
          {files.length > 0 && (
            <div className="flex gap-3 mt-6">
              <button
                onClick={uploadAllFiles}
                disabled={isUploading || files.every(f => f.status !== 'pending')}
                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isUploading ? t('upload.uploadStarted') : t('common.upload')}
              </button>

              <button
                onClick={async () => {
                  const failedFiles = files.filter(f => f.status === 'error')
                  // Reset all failed files to pending
                  setFiles(prev => prev.map(f =>
                    f.status === 'error' ? { ...f, status: 'pending', error: undefined, progress: 0 } : f
                  ))
                  // Retry all failed files
                  setIsUploading(true)
                  for (const file of failedFiles) {
                    await uploadFile(file)
                  }
                  setIsUploading(false)
                }}
                disabled={!files.some(f => f.status === 'error')}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                🔄 Retry Failed
              </button>

              <button
                onClick={clearCompleted}
                disabled={!files.some(f => f.status === 'completed')}
                className="px-6 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Clear Completed
              </button>
            </div>
          )}

          {/* File List */}
          {files.length > 0 && (
            <div className="mt-6 space-y-3">
              <h3 className="text-lg font-medium text-gray-900">{t('image.uploadProgress')}</h3>
              {files.map(file => (
                <div
                  key={file.id}
                  className="flex items-center justify-between p-4 bg-gray-50 rounded-lg"
                >
                  <div className="flex items-center space-x-3 flex-1">
                    <span className="text-2xl">{getStatusIcon(file.status)}</span>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">
                        {file.file.name}
                      </p>
                      <p className="text-sm text-gray-500">
                        {formatFileSize(file.file.size)} • {file.uploadMethod === 'chunked' ? 'Chunked Upload' : 'Direct Upload'}
                      </p>
                      {file.error && (
                        <p className="text-sm text-red-600 mt-1">{file.error}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center space-x-4">
                    <div className="text-right">
                      <p className={`text-sm font-medium ${getStatusColor(file.status)}`}>
                        {file.status.charAt(0).toUpperCase() + file.status.slice(1)}
                      </p>
                      {file.status === 'uploading' && (
                        <p className="text-xs text-gray-500">
                          {file.progress}%
                          {file.progress >= 95 && file.progress < 100 && ' (Finalizing...)'}
                        </p>
                      )}
                      {file.status === 'completed' && file.imageId && (
                        <Link
                          to={`/imaging/viewer/${file.imageId}`}
                          className="text-xs text-blue-600 hover:text-blue-800"
                        >
                          View Image
                        </Link>
                      )}
                    </div>

                    {file.status === 'uploading' && (
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full transition-all"
                          style={{ width: `${file.progress}%` }}
                        />
                      </div>
                    )}

                    {file.status === 'pending' && (
                      <button
                        onClick={() => removeFile(file.id)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        ✕
                      </button>
                    )}

                    {file.status === 'error' && (
                      <div className="flex space-x-2">
                        <button
                          onClick={async () => {
                            // Reset file status to pending
                            setFiles(prev => prev.map(f =>
                              f.id === file.id ? { ...f, status: 'pending', error: undefined, progress: 0 } : f
                            ))
                            // Retry upload
                            await uploadFile(file)
                          }}
                          className="px-3 py-1 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          🔄 Retry
                        </button>
                        <button
                          onClick={() => removeFile(file.id)}
                          className="text-gray-400 hover:text-red-600"
                        >
                          ✕
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Info Section */}
          <div className="mt-8 p-4 bg-green-50 rounded-lg">
            <h4 className="font-medium text-green-900 mb-2">📤 {t('image.simpleReliable')}</h4>
            <ul className="text-sm text-green-800 space-y-1">
              <li>• {t('image.directUpload')}</li>
              <li>• {t('image.worksAllSizes')}</li>
              <li>• {t('image.realtimeProgress')}</li>
              <li>• {t('image.autoTileProcessing')}</li>
              <li>• {t('image.errorRecovery')}</li>
              <li>• {t('image.noAuthIssues')}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  )
}

export default SimpleImageUpload